/*
 * Algolia Insights: click + add-to-cart conversion tracking.
 *
 * Ports the FoxEcom integration's `algolia_analytics.js` behaviour onto this
 * theme's modern stack. Reads credentials from `window.algoliaConfig` (emitted
 * server-side by `algolia-config.liquid`) and drives the vendored search-insights
 * UMD build, whose queue-style client lives at `window.AlgoliaAnalytics.default`.
 *
 * Flow: a product-hit click (from either the collection takeover or predictive
 * search) sends `clickedObjectIDsAfterSearch` and is stashed in localStorage;
 * the next add-to-cart converts those stashed objects via
 * `convertedObjectIDsAfterSearch`, then clears the stash. Hits carry their
 * Algolia coordinates in `data-algolia-objectid/queryid/position`, set by the
 * two engines (`clickAnalytics: true` makes the queryID available).
 *
 * Self-gates: does nothing unless analytics is enabled in the store metafields
 * and the vendored library actually loaded.
 */
(function () {
  'use strict';

  var cfg = window.algoliaConfig;
  if (!cfg || !cfg.analytics_enabled || !cfg.app_id || !cfg.search_api_key) return;

  var lib = window.AlgoliaAnalytics;
  var aa = lib && lib.default;
  if (typeof aa !== 'function') {
    console.warn('[algolia-insights] vendored search-insights failed to load');
    return;
  }

  var baseIndex = (cfg.index_prefix || '') + 'products';
  var STORAGE_KEY = 'algolia_analytics_clicked_objects';

  // ---- Client init -----------------------------------------------------
  // A random token keeps anonymous sessions coherent; with cookie consent
  // enabled we defer to Shopify's Customer Privacy API before persisting one.
  var randomUserToken = Array.from(Array(20), function () {
    return Math.floor(Math.random() * 36).toString(36);
  }).join('');

  if (cfg.usertoken_with_cookies === 'enabled' && window.Shopify && window.Shopify.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (error) {
        console.error('[algolia-insights] Customer Privacy API error', error);
      }
      var canTrack = window.Shopify.customerPrivacy
        ? window.Shopify.customerPrivacy.userCanBeTracked()
        : false;
      aa('init', { appId: cfg.app_id, apiKey: cfg.search_api_key, useCookie: canTrack });
      if (!canTrack) aa('setUserToken', randomUserToken);
    });
  } else {
    aa('init', { appId: cfg.app_id, apiKey: cfg.search_api_key });
    aa('setUserToken', randomUserToken);
  }

  // ---- Conversion stash (edge: localStorage / JSON may throw) ----------
  function saveForConversion(clickData) {
    try {
      var stashed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      var data = Object.assign({}, clickData);
      // The conversion event is a different name and has no positions.
      data.eventName = 'Added to cart';
      delete data.positions;
      stashed.push(data);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stashed));
    } catch (e) {
      // Storage unavailable or quota exceeded — drop the event silently.
    }
  }

  function trackConversion() {
    aa('getUserToken', null, function (err, userToken) {
      if (err) {
        console.error(err);
        return;
      }
      try {
        var stashed = localStorage.getItem(STORAGE_KEY);
        if (stashed) {
          JSON.parse(stashed).forEach(function (data) {
            aa('convertedObjectIDsAfterSearch', Object.assign({}, data, { userToken: userToken }));
          });
        }
      } catch (e) {
        // ignore parse/storage errors
      }
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore
      }
    });
  }

  // ---- Click tracking (delegated, covers takeover + predictive) --------
  // One listener serves both surfaces: any product link inside a card stamped
  // with `data-algolia-objectid` records a click-after-search. Swatch buttons
  // (which preventDefault and rewrite links in place) carry no objectID, so
  // they never trigger this.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!link) return;
    var card = link.closest('[data-algolia-objectid]');
    if (!card) return;

    var objectID = card.getAttribute('data-algolia-objectid');
    var queryID = card.getAttribute('data-algolia-queryid');
    if (!objectID || !queryID) return; // no query context — not a tracked search hit

    var position = parseInt(card.getAttribute('data-algolia-position'), 10);
    var clickData = {
      index: card.getAttribute('data-algolia-index') || baseIndex,
      eventName: 'click',
      queryID: queryID,
      objectIDs: [objectID]
    };
    if (!isNaN(position)) clickData.positions = [position];

    aa('clickedObjectIDsAfterSearch', clickData);
    saveForConversion(clickData);
  });

  // ---- Add-to-cart conversion -----------------------------------------
  // The product form submit button carries `data-algolia-atc`. The element is
  // present at parse time on product pages, so a direct listener is enough.
  var atc = document.querySelector('[data-algolia-atc]');
  if (atc) {
    atc.addEventListener('click', trackConversion);
  }

  window.AlgoliaInsights = { client: aa, trackConversion: trackConversion };
})();
